import { UserPlusIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    CustomListItemId,
    CustomListKey,
    defaultClosedPaidDuration,
    HomeCareLeadId,
    Maybe,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureInput, inferProcedureOutput } from "@trpc/server";
import { PageHeader } from "components/layout";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import * as React from "react";
import { Link, useHistory } from "react-router-dom";
import { useCustomList } from "store/selectors/bridge";
import {
    useFeature,
    usePipelineState,
    useRegionId,
    useUsers,
} from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { NO_NAME } from "util/text";
import { handleEvent } from "util/user-events";
import { ClientSearch } from "../clients/client-search/client-search";
import { trpc, trpcClient } from "../rpc/trpc";
import { PipelineInner } from "./pipeline-inner";
import { badgeColorForNextTask, NextActionLine } from "./task-badge";
import type { PipelineCardRenderProps, PipelineConfig, PipelineItem } from "./types";

type HomeCareLeadOutput = inferProcedureOutput<AppRouter["homeCareLead"]["list"]>[number];
type HomeCareLeadListInput = inferProcedureInput<AppRouter["homeCareLead"]["list"]>;

const homeCareLeadCardType = "HomeCareLeadCard" as const;

function homeCareLeadListInput(
    searchCriteria: Record<string, unknown>,
    statusId: CustomListItemId,
    statusChangedAfter: string | null,
): HomeCareLeadListInput {
    return {
        ...searchCriteria,
        statusListItemId: statusId,
        statusChangedAfter,
    } as HomeCareLeadListInput;
}

function useClosedStatusCutoff(): (statusId: CustomListItemId) => string | null {
    const { itemHasCategory } = useCustomList(CustomListKey.HomeCareLeadStatus);
    const { settings } = useTenantSettings();
    const closedPaidDuration = settings.closedPaidDuration;
    return React.useCallback(
        (statusId: CustomListItemId) => {
            if (!itemHasCategory(statusId, "Closed")) {
                return null;
            }
            const days = closedPaidDuration ?? defaultClosedPaidDuration;
            return CalendarDate.today.addDays(-days).toString();
        },
        [itemHasCategory, closedPaidDuration],
    );
}

function useHomeCareLeadColumnQuery(
    statusId: CustomListItemId,
    searchCriteria: Record<string, unknown>,
): { data: PipelineItem[] | undefined } {
    const trpcContext = trpc.useTRPC();
    const closedStatusCutoff = useClosedStatusCutoff();
    const query = useQuery({
        ...trpcContext.homeCareLead.list.queryOptions(
            homeCareLeadListInput(searchCriteria, statusId, closedStatusCutoff(statusId)),
        ),
    });
    return {
        data: query.data ? (query.data as PipelineItem[]) : undefined,
    };
}

const HomeCareLeadCard: React.FC<{
    client: HomeCareLeadOutput;
    highlightCard: boolean;
    pendingCard: boolean;
    showAssignedTo: boolean;
    getClientStatus: (item: PipelineItem) => {
        descriptor: string;
        date: Maybe<CalendarDate>;
    };
}> = props => {
    const users = useUsers().users;
    const displayName = props.client.name || NO_NAME;
    const bestContactName = props.client.bestContactName;
    const assignedUser = props.client.assignedUserId
        ? users[props.client.assignedUserId]
        : null;
    const status = props.getClientStatus(props.client as unknown as PipelineItem);

    return (
        <div
            className={`space-y-2 pipeline-card pipeline-client-card ${
                props.highlightCard ? "highlight-card" : ""
            }`}>
            <div>
                <Link
                    to={`/home-care-clients/show/${props.client.id}`}
                    className={defaultLinkStyle}>
                    <div className="flex gap-1 justify-between">
                        <div className="flex-1">
                            <div className="font-bold">{displayName}</div>
                            {bestContactName ? (
                                <div className="text-xs text-gray-600">
                                    {bestContactName}
                                </div>
                            ) : null}
                        </div>
                        <div className="flex-none">
                            {props.pendingCard ? (
                                <Spinner />
                            ) : (
                                <div
                                    className={`${badgeColorForNextTask(
                                        props.client.nextTask,
                                    )} rounded-full w-3 h-3`}
                                />
                            )}
                        </div>
                    </div>
                </Link>
            </div>
            <NextActionLine nextTask={props.client.nextTask} />
            <div className="text-xs">
                {status.descriptor}:{" "}
                {status.date.map(x => x.humanFriendly()).getOrElse("(not specified)")}
            </div>
            {props.showAssignedTo ? (
                <p className="text-xs italic">
                    {assignedUser ? assignedUser.name : "Unassigned"}
                </p>
            ) : null}
        </div>
    );
};

function useHomeCareLeadPipelineConfig(): PipelineConfig {
    const queryClient = useQueryClient();
    const closedStatusCutoff = useClosedStatusCutoff();

    const onDragDrop = React.useCallback(
        async (
            entityId: string,
            oldStatusId: CustomListItemId,
            newStatusId: CustomListItemId,
            searchCriteria: Record<string, unknown>,
        ) => {
            handleEvent("home-care-client-status-change", {
                source: "pipeline",
            });

            const oldQueryKey = [
                ["homeCareLead", "list"],
                {
                    input: homeCareLeadListInput(
                        searchCriteria,
                        oldStatusId,
                        closedStatusCutoff(oldStatusId),
                    ),
                    type: "query",
                },
            ];
            const oldQueryData: HomeCareLeadOutput[] | undefined =
                queryClient.getQueryData(oldQueryKey);
            const oldClient = oldQueryData?.find(c => c.id === entityId);

            if (oldClient) {
                queryClient.setQueryData(
                    oldQueryKey,
                    (oldStatusData: HomeCareLeadOutput[]) => {
                        return produce(oldStatusData, (draft: HomeCareLeadOutput[]) => {
                            const index = draft.findIndex(c => c.id === entityId);
                            if (index !== -1) {
                                draft.splice(index, 1);
                            }
                        });
                    },
                );
                const newQueryKey = [
                    ["homeCareLead", "list"],
                    {
                        input: homeCareLeadListInput(
                            searchCriteria,
                            newStatusId,
                            closedStatusCutoff(newStatusId),
                        ),
                        type: "query",
                    },
                ];
                queryClient.setQueryData(
                    newQueryKey,
                    (newStatusData: HomeCareLeadOutput[]) => {
                        return produce(newStatusData, (draft: HomeCareLeadOutput[]) => {
                            draft.unshift(oldClient);
                        });
                    },
                );
            }

            await trpcClient.homeCareLead.update.mutate({
                id: entityId as HomeCareLeadId,
                statusListItemId: newStatusId,
            });

            queryClient.refetchQueries({
                queryKey: [["homeCareLead", "list"]],
            });
        },
        [queryClient, closedStatusCutoff],
    );

    const renderCard = React.useCallback(
        (item: PipelineItem, renderProps: PipelineCardRenderProps) => (
            <HomeCareLeadCard
                client={item as unknown as HomeCareLeadOutput}
                highlightCard={renderProps.highlightCard}
                pendingCard={renderProps.pendingCard}
                showAssignedTo={renderProps.showAssignedTo}
                getClientStatus={renderProps.getStatusDescription}
            />
        ),
        [],
    );

    return React.useMemo(
        () => ({
            customListKey: CustomListKey.HomeCareLeadStatus,
            cardType: homeCareLeadCardType,
            useColumnQuery: useHomeCareLeadColumnQuery,
            onDragDrop,
            renderCard,
        }),
        [onDragDrop, renderCard],
    );
}

const AddHomeCareLeadButton: React.FC = () => {
    const { push } = useHistory();
    const regionId = useRegionId();
    const [isCreating, setIsCreating] = React.useState(false);

    const onClick = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (!regionId || isCreating) return;
            setIsCreating(true);
            handleEvent("create-home-care-client", { source: "button" });
            try {
                const result = await trpcClient.homeCareLead.create.mutate({
                    name: "",
                    regionId,
                });
                push(`/home-care-clients/show/${result.id}`);
            } finally {
                setIsCreating(false);
            }
        },
        [regionId, isCreating, push],
    );

    if (!regionId) {
        return null;
    }

    return (
        <PrimaryButton onClick={onClick} disabled={isCreating}>
            <div className="flex items-center space-x-2">
                <UserPlusIcon className="w-5 h-5" />
                <span className="text-sm">New home care client</span>
            </div>
        </PrimaryButton>
    );
};

export const HomeCareLeadPipeline: React.FC = () => {
    const config = useHomeCareLeadPipelineConfig();
    const isEnabled = useFeature("homeCarePrototype");
    const pipelineState = usePipelineState();

    if (!isEnabled) {
        return null;
    }

    return (
        <div className="flex flex-col md:inset-0 md:absolute">
            <PageHeader title="Home Care Pipeline">
                <AddHomeCareLeadButton />
            </PageHeader>
            <div className="px-4 py-2">
                <ClientSearch includeCardSort />
            </div>
            <div className="pipeline-wrapper bg-brand-700 flex px-4 py-2 flex-1 h-screen md:h-auto md:overflow-hidden">
                {pipelineState && pipelineState.isReady ? (
                    <PipelineInner
                        config={config}
                        searchCriteria={pipelineState.searchCriteria}
                    />
                ) : null}
            </div>
        </div>
    );
};
